import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuickActionToolbar = ({ selectedAssets, onAction }) => {
  const [showBulkRenewal, setShowBulkRenewal] = useState(false);
  const [showVendorLookup, setShowVendorLookup] = useState(false);
  const [renewalPeriod, setRenewalPeriod] = useState('12months');
  const [searchVendor, setSearchVendor] = useState('');

  const renewalPeriodOptions = [
    { value: '12months', label: '12 Months' },
    { value: '24months', label: '24 Months' },
    { value: '36months', label: '36 Months' }
  ];

  const vendorContacts = [
    {
      vendor: 'Dell Technologies',
      contact: 'Sarah Mitchell',
      email: 'sarah.mitchell@dell.com',
      phone: '+1 (555) 123-4567',
      accountManager: 'Enterprise Sales',
      lastContact: '2025-08-15'
    },
    {
      vendor: 'HP Inc.',
      contact: 'Michael Chen',
      email: 'michael.chen@hp.com',
      phone: '+1 (555) 234-5678',
      accountManager: 'Business Solutions',
      lastContact: '2025-08-10'
    },
    {
      vendor: 'Lenovo',
      contact: 'Emily Rodriguez',
      email: 'emily.rodriguez@lenovo.com',
      phone: '+1 (555) 345-6789',
      accountManager: 'Corporate Sales',
      lastContact: '2025-08-12'
    },
    {
      vendor: 'Apple Inc.',
      contact: 'David Kim',
      email: 'david.kim@apple.com',
      phone: '+1 (555) 456-7890',
      accountManager: 'Enterprise Team',
      lastContact: '2025-08-08'
    }
  ];

  const quickActions = [
    {
      id: 'bulk-renew',
      label: 'Bulk Renewal',
      icon: 'RefreshCw',
      description: 'Process warranty renewals for selected assets',
      color: 'text-success bg-success/10',
      action: () => setShowBulkRenewal(true)
    },
    {
      id: 'vendor-lookup',
      label: 'Vendor Contacts',
      icon: 'Phone',
      description: 'Access vendor contact information',
      color: 'text-accent bg-accent/10',
      action: () => setShowVendorLookup(true)
    },
    {
      id: 'quote-request',
      label: 'Request Quotes',
      icon: 'FileText',
      description: 'Generate quote requests for renewals',
      color: 'text-warning bg-warning/10',
      action: () => onAction('quote-request')
    },
    {
      id: 'replacement-order',
      label: 'Order Replacements',
      icon: 'ShoppingCart',
      description: 'Initiate replacement asset orders',
      color: 'text-error bg-error/10',
      action: () => onAction('replacement-order')
    }
  ];

  const handleBulkRenewal = () => {
    onAction('bulk-renewal', { period: renewalPeriod, assets: selectedAssets });
    setShowBulkRenewal(false);
  };

  const filteredVendors = vendorContacts?.filter(vendor =>
    vendor?.vendor?.toLowerCase()?.includes(searchVendor?.toLowerCase()) ||
    vendor?.contact?.toLowerCase()?.includes(searchVendor?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Quick Actions Grid */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Zap" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Quick Actions</h3>
            <p className="text-sm text-muted-foreground">Streamlined warranty management workflows</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions?.map((action) => (
            <button
              key={action?.id}
              onClick={action?.action}
              className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-smooth text-left group"
            >
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${action?.color} group-hover:scale-110 transition-transform`}>
                <Icon name={action?.icon} size={24} />
              </div>
              <h4 className="font-medium text-card-foreground mb-1">{action?.label}</h4>
              <p className="text-sm text-muted-foreground">{action?.description}</p>
            </button>
          ))}
        </div>
      </div>
      {/* Bulk Renewal Modal */}
      {showBulkRenewal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-md mx-4">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-card-foreground">Bulk Warranty Renewal</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowBulkRenewal(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                <p className="text-sm text-accent font-medium">
                  {selectedAssets?.length} asset{selectedAssets?.length > 1 ? 's' : ''} selected for renewal
                </p>
              </div>

              <Select
                label="Renewal Period"
                options={renewalPeriodOptions}
                value={renewalPeriod}
                onChange={setRenewalPeriod}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Renewal Options</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Auto-approve renewals under ₹1,000</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm">Request extended warranty options</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" defaultChecked />
                    <span className="text-sm">Send notifications to asset owners</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkRenewal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleBulkRenewal}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Process Renewals
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Vendor Lookup Modal */}
      {showVendorLookup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-card-foreground">Vendor Contact Directory</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowVendorLookup(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6">
              <Input
                type="search"
                placeholder="Search vendors or contacts..."
                value={searchVendor}
                onChange={(e) => setSearchVendor(e?.target?.value)}
                className="mb-4"
              />

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {filteredVendors?.map((vendor, index) => (
                  <div key={index} className="p-4 border border-border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-card-foreground mb-1">{vendor?.vendor}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                          <div>
                            <p><strong>Contact:</strong> {vendor?.contact}</p>
                            <p><strong>Email:</strong> {vendor?.email}</p>
                          </div>
                          <div>
                            <p><strong>Phone:</strong> {vendor?.phone}</p>
                            <p><strong>Team:</strong> {vendor?.accountManager}</p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Last contact: {new Date(vendor.lastContact)?.toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          variant="ghost"
                          size="icon"
                          iconName="Mail"
                          title="Send email"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          iconName="Phone"
                          title="Call vendor"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-border flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowVendorLookup(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Keyboard Shortcuts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-muted/50 rounded-lg flex items-center justify-center">
            <Icon name="Keyboard" size={20} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Keyboard Shortcuts</h3>
            <p className="text-sm text-muted-foreground">Speed up your warranty management workflow</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Select all assets</span>
              <kbd className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">Ctrl + A</kbd>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Bulk renewal</span>
              <kbd className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">Ctrl + R</kbd>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Search assets</span>
              <kbd className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">Ctrl + F</kbd>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Export data</span>
              <kbd className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">Ctrl + E</kbd>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Vendor lookup</span>
              <kbd className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">Ctrl + V</kbd>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Refresh data</span>
              <kbd className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">F5</kbd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActionToolbar;